import { hostRecordingCatalogPageSchema, type HostRecordingCatalogPage, type PlaycastMessage, type PlaycastMessageTarget } from '@playcastdotio/Messaging';
import type { IIpcService } from '../ipc/IIpcService';
import { playcastEnvelope } from './NativeMacroPackageBridge';

interface Pending { resolve(page: HostRecordingCatalogPage): void; reject(error: Error): void; timer: ReturnType<typeof setTimeout> }

/** Reads verified disk metadata through trusted main IPC; never starts a transfer. */
export class RawPackageCatalogIpcClient {
  private readonly pending = new Map<string, Pending>();
  public constructor(private readonly ipc: Pick<IIpcService, 'send'>) {}

  public page(campaignId: string, cursor?: string): Promise<HostRecordingCatalogPage> {
    const requestId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { this.pending.delete(requestId); reject(new Error('recording_catalog_timeout')); }, 120_000);
      this.pending.set(requestId, { resolve, reject, timer });
      try { this.ipc.send(playcastEnvelope('host', 'macroPackageList', false, { requestId, campaignId, ...(cursor ? { cursor } : {}) })); }
      catch { this.settle(requestId)?.reject(new Error('recording_catalog_unavailable')); }
    });
  }

  public receive(message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    if (message.header.target !== 'host' || (message.header.action as string) !== 'macroPackageListComplete' || !message.header.isReply) return false;
    const body = message.body.message as Record<string, unknown> | undefined, pending = this.settle(String(body?.requestId ?? ''));
    if (!pending) return true;
    const page = hostRecordingCatalogPageSchema.safeParse(body?.page);
    if (body?.ok !== true || !page.success) pending.reject(new Error('recording_catalog_verification_failed'));
    else pending.resolve(page.data);
    return true;
  }

  public disconnect(): void {
    for (const id of [...this.pending.keys()]) this.settle(id)?.reject(new Error('raw_package_upload_disconnected'));
  }
  private settle(requestId: string): Pending | undefined {
    const pending = this.pending.get(requestId);
    if (pending) { clearTimeout(pending.timer); this.pending.delete(requestId); }
    return pending;
  }
}
